import React, { useState, useEffect } from 'react'
import useStore, { resolveEffectiveTheme } from 'state/knovStore'
import AddStream from 'components/quests/AddStream'
import cn from 'classnames'
import CopyTooltip from 'components/shared/CopyTooltip'
import { insertToClipboard } from '../../utils'
import WithTooltip from 'components/shared/WithTooltip'
import { showTooltipConfirmation } from 'state/imperativeApis/tooltipApi'
import api from 'api/api'
import { radioNowFeed } from 'state/radioNowFeed'
import { subscribeRadioListenerCount } from 'state/radioListenerCount'

import ProfileIcon from 'components/shared/icons/ProfileIcon'
import TeamIcon from 'components/users/TeamIcon'
import PrivateIcon from 'components/shared/icons/PrivateIcon'
import DirectMessagesIcon from 'components/shared/icons/DirectMessagesIcon'
import LinkIcon from 'components/shared/icons/LinkIcon'
import ClipIcon from 'components/shared/icons/ClipIcon'
import SearchIcon from 'components/shared/icons/SearchIcon'
import PublicIcon from 'components/shared/icons/PublicIcon'
import HomeIcon from 'components/shared/icons/HomeIcon'
import TreechatIconPng from 'components/shared/icons/TreechatIconPng'
import NotiIcon from 'components/shared/icons/NotiIcon'
import HistoryIcon from 'components/shared/icons/HistoryIcon'
import StarredIcon from 'components/shared/icons/StarredIcon'
import { isMobile } from 'lib/device'
import useStreamFilters from 'components/filters/useStreamFilters'
import { getCenterPanel } from 'state/imperativeApis/swiperApi'
import {
    Wallet,
    Circle,
    Store,
    Headphones,
    ListMusic,
    Radio,
    Settings,
    TrendingUp,
    MessageCircle,
    ShieldCheck,
} from 'lucide-react'

import perms from 'lib/perms'
import NewQuestButton from '../quests/NewQuestButton'
import NewQuestButtonUIMainWrapper from '../quests/NewQuestButtonUIMainWrapper'
import OrdinalsIcon from 'components/shared/OrdinalsIcon'
import { TREECHAT_DEVS } from 'constants/treechatDevs'
import StreamsIcon from 'components/shared/icons/StreamsIcon'
import MemesIcon from 'components/shared/icons/MemesIcon'
import { homePrivateEnabled } from 'lib/homePrivate'
import { memesEnabled } from 'lib/memesFeature'
import { treechatRadioEnabled } from 'lib/treechatRadio'

import { defaultPrivateStreamFilter } from 'lib/myStreamsFeature'
import QuestHistory from 'components/filters/QuestHistory'
import { directMessagesEnabled } from 'lib/directMessages'
import styles from 'components/shared/filter-options.module.scss'
import WalletBalanceAmount from 'components/wallet/WalletBalanceAmount'
import { fixedHeaderEnabled } from 'lib/fixedHeader'
import {
    openMediaDockPlaylist,
    openMediaDockRadio,
} from 'components/shared/MediaDock/mediaDockEvents'
import { consumeSpaceNotificationBadge } from 'refactor/hooks/api/notificationBadgeRefetchPolicy'

// A station read this recent is fresh enough: pointing at Music again reuses it.
const RADIO_PREFETCH_FRESH_MS = 15 * 1000

export default function FilterOptions(props) {
    const appearanceTheme = useStore(state => state.appearanceTheme)
    const effectiveTheme = resolveEffectiveTheme(appearanceTheme)
    const resolvedTheme = props.theme || effectiveTheme
    const lightTheme = resolvedTheme === 'light'
    const user = gon && gon.currentUser
    const guestJoinCta = Boolean(props.allowGuestNewQuest && !user)
    const disableNewQuestTooltip = Boolean(props.disableNewQuestTooltip)
    const showIncludes = key => props.show.includes(key)
    // FilterOptions can sometimes be mounted outside of a Panel as in PanelController.
    // In that case we default to the center panel.
    const panelId = props.panelId || getCenterPanel()?.panelId
    const filters = useStreamFilters(panelId, { append: !!props.append })
    const filter = filters.filter
    const [showHiddenStreams, setShowHiddenStreams] = useState(false)
    const [hiddenStreams, setHiddenStreams] = useState([])
    const [loadingHidden, setLoadingHidden] = useState(false)

    const openWalletModal = useStore(state => state.actions.modalActions.openWalletModal)
    const openEditProfileModal = useStore(state => state.actions.modalActions.openEditProfileModal)
    const openOrdinalsModal = useStore(state => state.actions.modalActions.openOrdinalsModal)
    const openMetricsModal = useStore(state => state.actions.modalActions.openMetricsModal)
    const openAdminModal = useStore(state => state.actions.modalActions.openAdminModal)

    const knovigatorSpace = useStore(state => state.knovigatorSpace)
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userSpaces = useStore(state => state.userSpaces)
    const userStreams = useStore(state => state.userStreams)
    const publicTeams = useStore(state => state.publicTeams)
    const numNoti = useStore(state => state.numNoti)
    const hasMemesPanel = memesEnabled()
    const hasTreechatRadio = treechatRadioEnabled()

    // The Radio stream: every Treechat Radio post is published to it, and it is
    // kept out of Public/Following so the station has one home.
    const radioTeamId = typeof gon !== 'undefined' ? gon.RADIO_TEAM_ID : null
    const isRadioSelected = Boolean(
        radioTeamId && String(filter?.team_ids?.[0] || '') === String(radioTeamId),
    )
    // Live audience next to the Radio item, from a count-only poll: reading
    // it never counts this viewer as a listener.
    const showRadioOption = Boolean(hasTreechatRadio && radioTeamId && showIncludes('radio'))
    const [radioListenerCount, setRadioListenerCount] = useState(null)
    useEffect(() => {
        if (!showRadioOption) return
        return subscribeRadioListenerCount(setRadioListenerCount)
    }, [showRadioOption])

    // Pointing at Music starts the station read its header waits on (without
    // the odds chart), so the song is usually there when the stream opens.
    // The feed keeps the snapshot while its song is on air.
    const prefetchRadio = () => {
        const latest = radioNowFeed.latest()
        if (latest?.receivedAt && Date.now() - latest.receivedAt < RADIO_PREFETCH_FRESH_MS) return
        void radioNowFeed.refresh({ contenders: false })
    }

    const selectRadio = () => {
        if (!radioTeamId) return
        // Also handles selecting an already-open, idle Radio panel.
        void radioNowFeed.refresh({ listening: true, fresh: true })
        window.logEv('CLICK_RADIO_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        // The station is chronological: entering it always puts the current
        // ON AIR room first, regardless of the previous stream's saved order.
        filters.selectTeam(radioTeamId, { order: 'time' })
        props.close()
    }

    const selectStarred = () => {
        window.logEv('CLICK_STARRED_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        filters.selectStarred()
        props.close()
    }

    const selectHistory = () => {
        window.logEv('CLICK_HISTORY_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        filters.selectHistory()
        props.close()
    }

    const selectUser = () => {
        const targetUser =
            typeof props.user === 'object' && props.user !== null ? props.user.id : props.user
        const targetUserId = targetUser || user?.id
        if (targetUserId) {
            filters.selectUser(targetUserId)
        }
        props.close()
    }

    const selectSearch = () => {
        const queryValue = filter?.query ?? ''
        filters.selectSearch({ all: true, query: queryValue })
        props.close()
    }

    const defaultPublicOrder = 'time'
    const homeIsFollowing = gon?.currentUser?.space_options?.defaultHomeStream === 'following'
    const streamsScope = filter?.streams_scope
    const isHomeSelected =
        filter?.public || filter?.following || (homePrivateEnabled() && filter?.private)
    const selectHome = () => {
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            if (homeIsFollowing) {
                filters.setStreamFilter({ following: true, search: true, query: filter.query })
            } else {
                filters.setStreamFilter({ public: true, search: true, query: filter.query })
            }
        } else {
            if (homeIsFollowing) {
                const order =
                    filters.isPublicSelected() || filters.isFollowingSelected()
                        ? filter.order
                        : undefined
                filters.selectFollowing(order ? { order } : undefined)
            } else {
                const order = filters.isPublicSelected() ? filter.order : defaultPublicOrder
                filters.selectPublic({ order })
            }
        }
        props.close()
    }

    const selectPublic = () => {
        if (props.origin === 'search') {
            filters.setStreamFilter({ public: true, search: true, query: filter.query })
        } else {
            const order = filters.isPublicSelected() ? filter.order : defaultPublicOrder
            filters.selectPublic({ order })
        }
        props.close()
    }

    const selectTreechat = () => {
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ treechat: true, search: true, query: filter.query })
        } else {
            const order = filters.isTreechatSelected() ? filter.order : defaultPublicOrder
            filters.selectTreechat({ order })
        }
        props.close()
    }

    const selectOrdinals = () => {
        window.logEv('CLICK_ORDINALS_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ ordinals: true, search: true, query: filter.query })
        } else {
            const order = filters.isOrdinalsSelected() ? filter.order : defaultPublicOrder
            filters.selectOrdinals({ order })
        }
        props.close()
    }

    const selectMarket = () => {
        window.logEv('CLICK_MARKET_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        if (props.origin === 'search') {
            filters.setStreamFilter({
                market: true,
                market_cards: true,
                search: true,
                query: filter.query,
            })
        } else {
            // Keep the order only when already on the Activity lens: an Items
            // order (price) must not leak into the card feed.
            const order = filter?.market_cards ? filter.order : defaultPublicOrder
            filters.selectMarket({ order })
        }
        props.close()
    }

    const selectPrivate = () => {
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({
                ...defaultPrivateStreamFilter(),
                search: true,
                query: filter.query,
            })
        } else {
            const order = filters.isPrivateSelected() ? filter.order : 'time'
            filters.selectPrivate({ order })
        }
        props.close()
    }

    // The DMs item's feed (and its message requests); Private is the rest.
    const directMessagesFeed = !!(filter?.private_messages || filter?.private_message_requests)

    const selectDirectMessages = () => {
        window.logEv('CLICK_DIRECT_MESSAGES_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        filters.selectNewFilter({ private: true, private_messages: true, order: 'time' })
        props.close()
    }

    const selectNotifications = () => {
        window.logEv('CLICK_NOTIFICATIONS_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        useStore.getState().set(state => consumeSpaceNotificationBadge(state, activeSpaceId))
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ notifications: true, search: true, query: filter.query })
        } else {
            const order = filters.isNotiSelected && filters.isNotiSelected() ? filter.order : 'time'
            filters.selectNotifications && filters.selectNotifications({ order })
        }
        props.close()
    }

    const selectClips = () => {
        window.logEv('CLICK_CLIPS_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ clip: true, search: true, query: filter.query })
        } else {
            const order = filters.isClipSelected() ? filter.order : 'time'
            filters.selectClips({ order })
        }
        props.close()
    }

    const selectPublicClips = () => {
        const order = filters.isPublicClipSelected() ? filter.order : 'time'
        filters.selectPublicClips({ order })
        props.close()
    }

    const selectStreams = () => {
        window.logEv('CLICK_STREAMS_DISCOVERY', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        filters.selectStreams()
        props.close()
    }

    const selectMemes = () => {
        window.logEv('CLICK_MEMES_DISCOVERY', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        filters.selectMemes()
        props.close()
    }

    const openPlaylist = () => {
        openMediaDockPlaylist('filter_options')
        props.close?.()
    }

    const openRadio = () => {
        openMediaDockRadio('filter_options')
        props.close?.()
    }

    const selectLinks = () => {
        window.logEv('CLICK_LINKS_STREAM', {
            previous_stream_type: filter?.public ? 'public' : filter?.private ? 'private' : 'other',
            from_location: 'filter_menu',
        })
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ link: true, search: true, query: filter.query })
        } else {
            const order = filters.isLinkSelected() ? filter.order : 'time'
            filters.selectLinks({ order })
        }
        props.close()
    }

    const selectTeam = teamId => {
        if (props.origin === 'search') {
            // In search mode, update filter while keeping search active
            filters.setStreamFilter({ team_ids: [teamId], search: true, query: filter.query })
        } else {
            const order = filters.isTeamSelected(teamId) ? filter.order : 'time'
            filters.selectTeam(teamId, { order })
        }
        props.close()
    }

    const loadHiddenStreams = async () => {
        if (loadingHidden || hiddenStreams.length > 0) return

        setLoadingHidden(true)
        try {
            // Get all teams including hidden ones
            const allTeams = await api.getTeams({ space_id: activeSpaceId, include_hidden: true })
            // Filter to only hidden teams (those not in userStreams)
            const visibleIds = userStreams.map(s => s.id)
            const hidden = allTeams.filter(team => !visibleIds.includes(team.id))
            setHiddenStreams(hidden)
        } catch (error) {
            console.error('Error loading hidden streams:', error)
        } finally {
            setLoadingHidden(false)
        }
    }

    const toggleHiddenStreams = () => {
        if (!showHiddenStreams && hiddenStreams.length === 0) {
            loadHiddenStreams()
        }
        setShowHiddenStreams(!showHiddenStreams)
    }

    const spaceName =
        userSpaces?.find(s => String(s.id) === String(activeSpaceId))?.name || knovigatorSpace?.name

    const myRef = React.useRef(null)

    useEffect(() => {
        if (!props.blur) return

        function handleClickOutside(ev) {
            if (myRef.current && !myRef.current.contains(ev.target)) {
                ev.stopPropagation()
                ev.preventDefault()
                setTimeout(() => {
                    props.close && props.close()
                }, 100)
            }
        }

        // Bind the event listener
        document.addEventListener('mouseup', handleClickOutside)

        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mouseup', handleClickOutside)
        }
    }, [myRef])

    const header = props.header
    const showAddStreamButton = Boolean(gon?.currentUser && showIncludes('teams'))
    const showMyStreamsHeader =
        !!gon?.currentUser && (showAddStreamButton || (userStreams?.length ?? 0) > 0)

    return (
        <div
            ref={myRef}
            className={cn(
                styles.filterOptionsComp,
                `filter-options-comp ${resolvedTheme}`,
                props.contextStyles,
            )}
        >
            <>
                {header && (
                    <div className={styles.spaceHeaderContainer}>
                        <div className={styles.spaceHeaderLabel}>{header}</div>
                    </div>
                )}

                <div
                    className={cn(
                        styles.filterOptionsScrollContainer,
                        lightTheme && styles.onePanelFilterOptionsScrollContainer,
                        isMobile && styles.overflow,
                    )}
                >
                    <div className={styles.streamSection}>
                        {showIncludes('home') && (
                            <FilterOption
                                key="home"
                                name="home"
                                filter={filter}
                                selected={isHomeSelected}
                                selectOption={selectHome}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show home stream."
                                close={props.close}
                                canThread
                                //contextStyles={cn(styles.publicStream, styles.headerStream)}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                                newThreadFilter={{ public: true }}
                            >
                                <HomeIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.homeIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            isHomeSelected &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Home</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('notifications') && (
                            <FilterOption
                                key="notifications"
                                name="notifications"
                                filter={filter}
                                selected={filter?.notifications}
                                selectOption={selectNotifications}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show your notifications."
                                close={props.close}
                                canThread
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <NotiIcon
                                    showIcon={true}
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.notiIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.notifications &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>
                                    Notifications
                                    {numNoti > 0 && (
                                        <span className={styles.notiBadge}>{numNoti}</span>
                                    )}
                                </div>
                            </FilterOption>
                        )}

                        {showIncludes('public') && (
                            <FilterOption
                                key="public"
                                name="public"
                                filter={filter}
                                selected={
                                    filter?.public ||
                                    (filter?.streams && !streamsScope) ||
                                    filter?.memes
                                }
                                selectOption={selectPublic}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show all public posts, channels, and memes."
                                close={props.close}
                                canThread
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <PublicIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.publicIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            (filter?.public ||
                                                (filter?.streams && !streamsScope) ||
                                                filter?.memes) &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Public</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('private') && (
                            <FilterOption
                                key="private"
                                name="private"
                                filter={filter}
                                selected={
                                    (filter?.private && !directMessagesFeed) ||
                                    streamsScope === 'private'
                                }
                                selectOption={selectPrivate}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show your private threads."
                                close={props.close}
                                canThread
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                                newThreadFilter={{ private: true }}
                            >
                                <PrivateIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.privateIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            ((filter?.private && !directMessagesFeed) ||
                                                streamsScope === 'private') &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Private</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('private') && directMessagesEnabled() && (
                            <FilterOption
                                key="direct_messages"
                                name="direct_messages"
                                filter={filter}
                                selected={directMessagesFeed}
                                selectOption={selectDirectMessages}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show your direct messages."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <DirectMessagesIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.privateIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            directMessagesFeed &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>DMs</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('ordinals') && (
                            <FilterOption
                                key="ordinals"
                                name="ordinals"
                                filter={filter}
                                selected={filter?.ordinals && !filter?.market}
                                selectOption={selectOrdinals}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show ordinals."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <div
                                    className={cn(
                                        styles.filterOptionIcon,
                                        styles.circleIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.ordinals &&
                                            !filter?.market &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                >
                                    <div className={styles.ordinalsAppChip}>
                                        <Circle />
                                    </div>
                                </div>

                                <div className={styles.streamLabel}>Ordinals</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('market') && (
                            <FilterOption
                                key="market"
                                name="market"
                                filter={filter}
                                selected={filter?.market}
                                selectOption={selectMarket}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Ordinals for sale, and market activity."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <div
                                    className={cn(
                                        styles.filterOptionIcon,
                                        styles.circleIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.market &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                >
                                    <div className={styles.ordinalsAppChip}>
                                        <Store />
                                    </div>
                                </div>

                                <div className={styles.streamLabel}>Market</div>
                            </FilterOption>
                        )}

                        {showIncludes('streams') && (
                            <FilterOption
                                key="streams"
                                name="streams"
                                filter={filter}
                                selected={filter?.streams}
                                selectOption={selectStreams}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Discover Channels"
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <StreamsIcon
                                    size={20}
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.streamsIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.streams &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />
                                <div className={styles.streamLabel}>Channels</div>
                            </FilterOption>
                        )}

                        {hasMemesPanel && showIncludes('memes') && (
                            <FilterOption
                                key="memes"
                                name="memes"
                                filter={filter}
                                selected={filter?.memes}
                                selectOption={selectMemes}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Discover Memes"
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <MemesIcon
                                    size={20}
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.streamsIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.memes &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />
                                <div className={styles.streamLabel}>Memes</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('user') && (
                            <FilterOption
                                key="user"
                                name="user"
                                filter={filter}
                                selected={filter?.user || filter?.user_ordinals}
                                selectOption={selectUser}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip={`Show ${spaceName} space's public threads.`}
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <ProfileIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.userIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            (filter?.user || filter?.user_ordinals) &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Profile</div>
                            </FilterOption>
                        )}

                        {showRadioOption && (
                            <FilterOption
                                key="radio"
                                name="radio"
                                filter={filter}
                                selected={isRadioSelected}
                                selectOption={selectRadio}
                                onIntent={prefetchRadio}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Treechat Music — the station and what people are playing."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <div
                                    className={cn(
                                        styles.filterOptionIcon,
                                        styles.radioIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            isRadioSelected &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                >
                                    <Radio strokeWidth={1.75} />
                                </div>

                                <div className={styles.streamLabel}>
                                    Music
                                    {radioListenerCount > 0 && (
                                        <span
                                            className={styles.radioListenerCount}
                                            title={`${radioListenerCount} listening now`}
                                        >
                                            <Headphones strokeWidth={2} />
                                            {radioListenerCount}
                                        </span>
                                    )}
                                </div>
                            </FilterOption>
                        )}

                        {showIncludes('search') && (
                            <FilterOption
                                key="search"
                                name="search"
                                filter={filter}
                                selected={filter?.search}
                                selectOption={selectSearch}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Search channel."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <SearchIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.searchIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.search &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Search</div>
                            </FilterOption>
                        )}

                        {user && showIncludes('clips') && (
                            <FilterOption
                                key="clips"
                                name="clips"
                                filter={filter}
                                selected={filter?.clip}
                                selectOption={selectClips}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show all saved clips."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <ClipIcon
                                    contextStyles={cn(
                                        styles.filterOptionIcon,
                                        styles.linkIcon,
                                        lightTheme && styles.onePanelFilterIcon,
                                        lightTheme &&
                                            filter?.clip &&
                                            styles.onePanelFilterIconSelected,
                                    )}
                                />

                                <div className={styles.streamLabel}>Clips</div>
                            </FilterOption>
                        )}
                    </div>

                    <div className={styles.streamSection}>
                        {user && showIncludes('starred') && (
                            <div className={styles.starredStreamContainer}>
                                <FilterOption
                                    key="starred"
                                    name="starred"
                                    append={props.append}
                                    filter={filter}
                                    selected={filter?.starred}
                                    selectOption={selectStarred}
                                    selectedStyle={props.selectedStyle}
                                    hoverStyle={props.hoverStyle}
                                    tip={`Show your starred threads in ${spaceName} space`}
                                    close={props.close}
                                    disableTooltips={props.disableTooltips}
                                >
                                    <StarredIcon
                                        showIcon
                                        contextStyles={cn(
                                            styles.starredIcon,
                                            lightTheme && styles.onePanelFilterIcon,
                                            lightTheme &&
                                                filter?.starred &&
                                                styles.onePanelFilterIconSelected,
                                        )}
                                    />

                                    <div className={styles.streamLabel}>Starred</div>
                                </FilterOption>
                            </div>
                        )}

                        {user && showIncludes('history') && (
                            <div className={styles.historyStreamContainer}>
                                <FilterOption
                                    key="history"
                                    name="history"
                                    append={props.append}
                                    filter={filter}
                                    selected={filter?.history}
                                    selectOption={selectHistory}
                                    selectedStyle={props.selectedStyle}
                                    hoverStyle={props.hoverStyle}
                                    tip={`Show your past actions in ${spaceName} space`}
                                    close={props.close}
                                    disableTooltips={props.disableTooltips}
                                >
                                    <HistoryIcon
                                        showIcon
                                        contextStyles={cn(
                                            styles.historyIcon,
                                            lightTheme && styles.onePanelFilterIcon,
                                            lightTheme &&
                                                filter?.history &&
                                                styles.onePanelFilterIconSelected,
                                        )}
                                    />

                                    <div className={styles.streamLabel}>History</div>
                                </FilterOption>
                            </div>
                        )}
                    </div>

                    {user && showIncludes('links') && (
                        <FilterOption
                            key="links"
                            name="links"
                            filter={filter}
                            selected={filter?.link}
                            selectOption={selectLinks}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyle}
                            tip="Show your shared-url threads."
                            close={props.close}
                            canThread
                            append={props.append}
                            disableTooltips={props.disableTooltips}
                        >
                            <LinkIcon
                                contextStyles={cn(
                                    styles.filterOptionIcon,
                                    styles.linkIcon,
                                    lightTheme && styles.onePanelFilterIcon,
                                    lightTheme && filter?.link && styles.onePanelFilterIconSelected,
                                )}
                            />

                            <div className={styles.streamLabel}>Link Access</div>
                        </FilterOption>
                    )}

                    {showIncludes('new-quest') && (
                        <div className={styles.onePanelNewQuestContainer}>
                            <NewQuestButton
                                panelId={panelId}
                                filter={filter}
                                append={props.append}
                                callback={() => props.close && props.close()}
                                place="right"
                                authSource="filter_options_join"
                                joinOnGuest={guestJoinCta}
                                disableTooltip={disableNewQuestTooltip}
                            >
                                <NewQuestButtonUIMainWrapper
                                    filter={filter}
                                    label={guestJoinCta ? 'Join' : 'New Thread'}
                                    showLabel
                                    showIcon={false}
                                    contextStyles={styles.newQuestButton}
                                />
                            </NewQuestButton>
                        </div>
                    )}

                    {user && showIncludes('quest-history') && (
                        <QuestHistory
                            panelId={panelId}
                            close={props.close}
                            append={props.append}
                            lightTheme={lightTheme}
                            disableTooltips={props.disableTooltips}
                        />
                    )}

                    {showIncludes('teams') && (
                        <>
                            {/* Show appropriate header based on authentication */}
                            {showMyStreamsHeader && (
                                <div className={styles.streamHeaderRow}>
                                    <div className={styles.streamHeaderRowLabel}>My Channels</div>
                                    {showAddStreamButton && (
                                        <AddStream
                                            closeStreamSelector={props.close}
                                            contextStyles={cn(
                                                styles.addStreamButton,
                                                lightTheme && styles.onePanelFilterIcon,
                                            )}
                                            panelId={panelId}
                                            filter={filter}
                                        />
                                    )}
                                </div>
                            )}
                            {!user && !!publicTeams?.length && (
                                <div className={styles.streamHeader}>Public Channels</div>
                            )}

                            {/* Show user streams for authenticated users */}
                            {user &&
                                !!userStreams?.length &&
                                userStreams.map(stream => {
                                    return (
                                        <FilterOption
                                            key={stream.id}
                                            name="team"
                                            filter={filter}
                                            team={stream}
                                            selected={
                                                filter?.team_ids &&
                                                filter?.team_ids?.includes(stream.id)
                                            }
                                            selectOption={() => selectTeam(stream.id)}
                                            selectedStyle={props.selectedStyle}
                                            hoverStyle={props.hoverStyle}
                                            tip={`Show threads and messages from ${stream.name}`}
                                            close={props.close}
                                            canThread
                                            append={props.append}
                                            disableTooltips={props.disableTooltips}
                                        >
                                            <div className={styles.teamIconContainer}>
                                                <TeamIcon
                                                    team={stream}
                                                    tip={`${stream.name} Channel`}
                                                    contextStyles={cn(
                                                        styles.filterOptionIcon,
                                                        styles.teamIcon,
                                                        lightTheme && styles.onePanelFilterIcon,
                                                    )}
                                                />
                                            </div>

                                            <div className={styles.streamName}>{stream.name}</div>

                                            <div className={styles.statusContainer}>
                                                {stream.public && (
                                                    <div
                                                        className={cn(
                                                            styles.statusComp,
                                                            lightTheme && styles.onePanelStatusComp,
                                                        )}
                                                    >
                                                        <i className="fa fa-globe" />
                                                    </div>
                                                )}

                                                {!stream.public && (
                                                    <div
                                                        className={cn(
                                                            styles.statusComp,
                                                            lightTheme && styles.onePanelStatusComp,
                                                        )}
                                                    >
                                                        <i className="fa fa-lock" />
                                                    </div>
                                                )}
                                            </div>
                                        </FilterOption>
                                    )
                                })}

                            {/* Show public teams for unauthenticated users */}
                            {!user &&
                                !!publicTeams?.length &&
                                publicTeams.map(stream => {
                                    return (
                                        <FilterOption
                                            key={stream.id}
                                            name="team"
                                            filter={filter}
                                            team={stream}
                                            selected={
                                                filter?.team_ids &&
                                                filter?.team_ids?.includes(stream.id)
                                            }
                                            selectOption={() => selectTeam(stream.id)}
                                            selectedStyle={props.selectedStyle}
                                            hoverStyle={props.hoverStyle}
                                            tip={`Show threads and messages from ${stream.name}`}
                                            close={props.close}
                                            canThread
                                            append={props.append}
                                            disableTooltips={props.disableTooltips}
                                        >
                                            <div className={styles.teamIconContainer}>
                                                <TeamIcon
                                                    team={stream}
                                                    tip={`${stream.name} Channel`}
                                                    contextStyles={cn(
                                                        styles.filterOptionIcon,
                                                        styles.teamIcon,
                                                        lightTheme && styles.onePanelFilterIcon,
                                                    )}
                                                />
                                            </div>

                                            <div className={styles.streamName}>{stream.name}</div>

                                            <div className={styles.statusContainer}>
                                                <div
                                                    className={cn(
                                                        styles.statusComp,
                                                        lightTheme && styles.onePanelStatusComp,
                                                    )}
                                                >
                                                    <i className="fa fa-globe" />
                                                </div>
                                            </div>
                                        </FilterOption>
                                    )
                                })}

                            {user && (
                                <div
                                    className={styles.streamHeader}
                                    onClick={toggleHiddenStreams}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span>
                                        Hidden Channels {loadingHidden && '(loading...)'}{' '}
                                        <i
                                            className={`fa fa-chevron-${
                                                showHiddenStreams ? 'up' : 'down'
                                            }`}
                                            style={{ marginLeft: '5px' }}
                                        />
                                    </span>
                                </div>
                            )}

                            {showHiddenStreams && hiddenStreams.length > 0 && (
                                <>
                                    {hiddenStreams.map(stream => {
                                        return (
                                            <FilterOption
                                                key={`hidden-${stream.id}`}
                                                name="team"
                                                filter={filter}
                                                team={stream}
                                                selected={false}
                                                selectOption={() => selectTeam(stream.id)}
                                                selectedStyle={props.selectedStyle}
                                                hoverStyle={props.hoverStyle}
                                                tip={`${stream.name} (Hidden)`}
                                                close={props.close}
                                                canThread
                                                append={props.append}
                                                isHidden={true}
                                                hiddenStreams={hiddenStreams}
                                                setHiddenStreams={setHiddenStreams}
                                                disableTooltips={props.disableTooltips}
                                            >
                                                <div className={styles.teamIconContainer}>
                                                    <TeamIcon
                                                        team={stream}
                                                        tip={`${stream.name} Channel (Hidden)`}
                                                        contextStyles={cn(
                                                            styles.filterOptionIcon,
                                                            lightTheme && styles.onePanelFilterIcon,
                                                        )}
                                                    />
                                                </div>

                                                <div
                                                    className={styles.streamName}
                                                    style={{ opacity: 0.6 }}
                                                >
                                                    {stream.name}
                                                </div>

                                                <div className={styles.statusContainer}>
                                                    <div
                                                        className={cn(
                                                            styles.statusComp,
                                                            lightTheme && styles.onePanelStatusComp,
                                                        )}
                                                    >
                                                        <i
                                                            className="fa fa-eye-slash"
                                                            style={{ opacity: 0.6 }}
                                                        />
                                                    </div>
                                                </div>
                                            </FilterOption>
                                        )
                                    })}
                                </>
                            )}

                            {showHiddenStreams && hiddenStreams.length === 0 && !loadingHidden && (
                                <div
                                    style={{
                                        padding: '10px',
                                        margin: '10px 20px',
                                        opacity: 0.6,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    No hidden channels
                                </div>
                            )}
                        </>
                    )}

                    {user && showIncludes('profile') && (
                        <FilterOption
                            key="profile"
                            name="user"
                            filter={filter}
                            selected={filter?.user || filter?.user_ordinals}
                            selectOption={selectUser}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyle}
                            tip="Show your profile."
                            close={props.close}
                            append={props.append}
                            disableTooltips={props.disableTooltips}
                        >
                            <ProfileIcon
                                contextStyles={cn(
                                    styles.filterOptionIcon,
                                    styles.userIcon,
                                    lightTheme && styles.onePanelFilterIcon,
                                    lightTheme &&
                                        (filter?.user || filter?.user_ordinals) &&
                                        styles.onePanelFilterIconSelected,
                                )}
                            />
                            <div className={styles.streamLabel}>Profile</div>
                        </FilterOption>
                    )}

                    {user && showIncludes('wallet') && (
                        <>
                            <FilterOption
                                key="wallet"
                                name="wallet"
                                filter={filter}
                                selected={false}
                                selectOption={() => openWalletModal()}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show wallet."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <div className={styles.walletIconContainer}>
                                    <div className={styles.walletIcon}>
                                        <Wallet size={20} />
                                    </div>
                                </div>

                                {/* fixed_header: the balance replaces the label (it
                                    left the top-right corner). */}
                                <div className={styles.streamLabel}>
                                    {fixedHeaderEnabled() ? <WalletBalanceAmount /> : 'Wallet'}
                                </div>
                            </FilterOption>

                            <FilterOption
                                key="playlist"
                                name="playlist"
                                filter={filter}
                                selected={false}
                                selectOption={openPlaylist}
                                selectedStyle={props.selectedStyle}
                                hoverStyle={props.hoverStyle}
                                tip="Show media playlist."
                                close={props.close}
                                append={props.append}
                                disableTooltips={props.disableTooltips}
                            >
                                <div className={styles.walletIconContainer}>
                                    <div className={styles.walletIcon}>
                                        <ListMusic size={20} />
                                    </div>
                                </div>

                                <div className={styles.streamLabel}>Playlist</div>
                            </FilterOption>
                        </>
                    )}

                    {user && showIncludes('account-ordinals') && (
                        <FilterOption
                            key="account-ordinals"
                            name="account-ordinals"
                            filter={filter}
                            selected={false}
                            selectOption={openOrdinalsModal}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyle}
                            tip="Show Ordinals."
                            close={props.close}
                            append={props.append}
                            disableTooltips={props.disableTooltips}
                        >
                            <div className={styles.walletIconContainer}>
                                <div className={styles.circleIcon}>
                                    <Circle
                                        size={20}
                                        className={cn(
                                            styles.filterOptionIcon,
                                            lightTheme && styles.onePanelFilterIcon,
                                        )}
                                    />
                                </div>
                            </div>

                            <div className={styles.streamLabel}>Manage Ordinals</div>
                        </FilterOption>
                    )}

                    {user && showIncludes('settings') && (
                        <FilterOption
                            key="settings"
                            name="settings"
                            filter={filter}
                            selected={false}
                            selectOption={() => {
                                openEditProfileModal()
                                props.close()
                            }}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyle}
                            tip="Show settings."
                            close={props.close}
                            append={props.append}
                            disableTooltips={props.disableTooltips}
                        >
                            <div className={styles.settingsIconContainer}>
                                <div className={styles.settingsIcon}>
                                    <Settings />
                                </div>
                            </div>

                            <div className={styles.streamLabel}>Settings</div>
                        </FilterOption>
                    )}

                    {TREECHAT_DEVS.includes(gon?.currentUser?.name) && showIncludes('metrics') && (
                        <FilterOption
                            key="metrics"
                            name="metrics"
                            filter={filter}
                            selectOption={openMetricsModal}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyles}
                            tip="Show metrics."
                            close={props.close}
                            disableTooltips={props.disableTooltips}
                        >
                            <div className={styles.metricsIcon}>
                                <TrendingUp size={24} />
                            </div>
                            <div className={styles.streamLabel}>Metrics</div>
                        </FilterOption>
                    )}

                    {gon?.currentUser?.knov_admin && showIncludes('admin') && (
                        <FilterOption
                            key="admin"
                            name="admin"
                            filter={filter}
                            selectOption={openAdminModal}
                            selectedStyle={props.selectedStyle}
                            hoverStyle={props.hoverStyles}
                            tip="Admin console (Hosts + Ops)."
                            close={props.close}
                            disableTooltips={props.disableTooltips}
                        >
                            <div className={styles.metricsIcon}>
                                <ShieldCheck size={24} />
                            </div>
                            <div className={styles.streamLabel}>Admin</div>
                        </FilterOption>
                    )}
                </div>
            </>
        </div>
    )
}

function FilterOption(props) {
    const [hover, setHover] = useState(false)
    const theme = useStore(state => state.appearanceTheme)
    const effectiveTheme = resolveEffectiveTheme(theme)
    const lightTheme = !isMobile && effectiveTheme === 'light'
    const onMouseEnter = ev => {
        // About to pick this option: let it start loading what it shows.
        props.onIntent?.()
        if (isMobile) return
        setHover(true)
    }
    const onMouseLeave = ev => {
        if (isMobile) return
        setHover(false)
    }

    const onClick = ev => {
        ev.preventDefault()
        ev.stopPropagation()

        if (props.selectOption) props.selectOption()
    }

    const selectedStyles = props.selected ? props.selectedStyle : null
    const hoverStyles = props.hoverStyle

    const team = props.team
    const isPinned = useStore(state => state.pinnedTeamIds[team?.id])
    const key = team ? team.id : props.name
    //console.log('key', key)

    const showControls = isMobile ? true : hover
    const showSettingsArea = showControls || isPinned

    const enhanceChild = child => {
        if (!React.isValidElement(child)) {
            return child
        }

        // Only add hoverStyles/selectedStyles to our custom components that expect them
        // Skip DOM elements (string types) and third-party components like lucide-react icons
        const childType =
            /** @type {string | (Function & {displayName?: string, name?: string})} */ (child.type)
        const componentName =
            (typeof childType !== 'string' && childType?.displayName) ||
            (typeof childType !== 'string' && childType?.name) ||
            ''
        const isCustomIconComponent =
            componentName.endsWith('Icon') ||
            componentName === 'TeamLogo' ||
            componentName === 'UserIcon'

        const extraProps =
            typeof child.type === 'string' || !isCustomIconComponent
                ? {}
                : {
                      hoverStyles,
                      selectedStyles,
                  }

        const childChildren = child.props?.children
        const enhancedChildren =
            childChildren !== undefined
                ? React.Children.map(childChildren, enhanceChild)
                : childChildren

        if (
            (extraProps && Object.keys(extraProps).length > 0) ||
            enhancedChildren !== childChildren
        ) {
            return React.cloneElement(child, extraProps, enhancedChildren)
        }

        return child
    }

    const childrenWithHover = React.Children.map(props.children, enhanceChild)

    // Don't render if the option is hidden
    if (props.hide) {
        return null
    }

    return (
        <div
            key={key}
            onClick={onClick}
            className={cn(
                styles.filterOptionComp,
                props.contextStyles,
                lightTheme && styles.onePanelFilterOptionComp,
                selectedStyles,
                hoverStyles,
            )}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onPointerDown={props.onIntent}
        >
            <WithTooltip
                tip={props.tip}
                contextStyles={styles.flexContainer}
                disable={props.disableTooltips}
            >
                <div className={styles.streamContainer}>{childrenWithHover}</div>

                {showSettingsArea && (
                    <div className={styles.settingsContainer}>
                        <StreamControls
                            name={props.name}
                            filter={props.filter}
                            team={team}
                            selectOption={props.selectOption}
                            close={props.close}
                            canThread={props.canThread}
                            append={props.append}
                            isHidden={props.isHidden}
                            hiddenStreams={props.hiddenStreams}
                            setHiddenStreams={props.setHiddenStreams}
                            disableTooltips={props.disableTooltips}
                            showControls={showControls}
                        />

                        {showControls && props.newThreadFilter && (
                            <NewStreamQuestBtn
                                panelId={props.panelId}
                                filter={props.newThreadFilter}
                                append={props.append}
                                close={props.close}
                            />
                        )}
                    </div>
                )}
            </WithTooltip>
        </div>
    )
}

function StreamControls(props) {
    const team = props.name == 'team' ? props.team : null
    const [isHiding, setIsHiding] = useState(false)
    const [pinHover, setPinHover] = useState(false)
    const isPinned = useStore(state => state.pinnedTeamIds[team?.id])
    const openStreamSettingsModal = useStore(
        state => state.actions.modalActions.openStreamSettingsModal,
    )

    // Commented out navigation to settings route - now using modal instead
    // const navigateTeamSettings = (ev, team) => {
    //     ev.preventDefault()
    //     ev.stopPropagation()
    //     location.href = `/stream/${team.id}/settings`
    // }

    const openTeamSettings = (ev, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        // Close the filter options first
        props.close()

        // Get panelId from props or use a default
        const panelId = props.panelId || getCenterPanel()?.panelId

        // Open the modal after a slight delay to ensure filter options is closed
        setTimeout(() => {
            openStreamSettingsModal(team, panelId, props.filter)
        }, 100)
    }

    const copyHandler = (ev, tipped, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (tipped.current) {
            insertToClipboard(team.url)
            showTooltipConfirmation(tipped.current, {
                content: 'Copied!',
                restoreContent: 'Copy',
                duration: isMobile ? 1000 : 2000,
                closeAfter: isMobile,
            })
        }
    }

    const pinHandler = async (ev, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        // Optimistically toggle pinned state in the store
        if (!isPinned) {
            useStore.getState().set(draft => {
                draft.pinnedTeamIds[team.id] = true
            })
            try {
                await api.pinTeam(team.id)
            } catch (error) {
                console.error('Error pinning team:', error)
                useStore.getState().set(draft => {
                    delete draft.pinnedTeamIds[team.id]
                })
            }
        } else {
            useStore.getState().set(draft => {
                delete draft.pinnedTeamIds[team.id]
            })
            try {
                await api.unpinTeam(team.id)
            } catch (error) {
                console.error('Error unpinning team:', error)
                useStore.getState().set(draft => {
                    draft.pinnedTeamIds[team.id] = true
                })
            }
        }

        // Refresh the teams list so sort order updates
        const { activeSpaceId } = useStore.getState()
        const teams = await api.getTeams({ space_id: activeSpaceId })
        useStore.getState().set({ userStreams: teams })
    }

    const hideHandler = async (ev, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (isHiding) return
        setIsHiding(true)

        try {
            await api.hideTeam(team.id)

            // Refresh the teams list to remove the hidden team
            const { activeSpaceId } = useStore.getState()
            const teams = await api.getTeams({ space_id: activeSpaceId })
            useStore.getState().set({ userStreams: teams })

            // Don't close the panel - just update the list
        } catch (error) {
            console.error('Error hiding team:', error)
        } finally {
            setIsHiding(false)
        }
    }

    const unhideHandler = async (ev, team) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (isHiding) return
        setIsHiding(true)

        try {
            await api.unhideTeam(team.id)

            // Refresh the teams list to add the unhidden team
            const { activeSpaceId } = useStore.getState()
            const teams = await api.getTeams({ space_id: activeSpaceId })
            useStore.getState().set({ userStreams: teams })

            // Also update the hidden streams list to remove the unhidden team
            const updatedHiddenStreams = props.hiddenStreams.filter(s => s.id !== team.id)
            props.setHiddenStreams(updatedHiddenStreams)

            // Don't close the panel - just update the list
        } catch (error) {
            console.error('Error unhiding team:', error)
        } finally {
            setIsHiding(false)
        }
    }

    return (
        <div className={styles.streamControlsComp}>
            {team && (
                <>
                    {props.showControls && !props.isHidden ? (
                        <WithTooltip tip="Hide this channel" disable={props.disableTooltips}>
                            <div
                                className={cn(styles.settingsBtnComp)}
                                onClick={ev => hideHandler(ev, team)}
                            >
                                <i className="fa fa-eye-slash" />
                            </div>
                        </WithTooltip>
                    ) : props.showControls && props.isHidden ? (
                        <WithTooltip tip="Unhide this channel" disable={props.disableTooltips}>
                            <div
                                className={cn(styles.settingsBtnComp)}
                                onClick={ev => unhideHandler(ev, team)}
                            >
                                <i className="fa fa-eye" />
                            </div>
                        </WithTooltip>
                    ) : null}

                    {props.showControls && !props.isHidden && props.canThread && (
                        <NewStreamQuestBtn
                            panelId={props.panelId}
                            team={team}
                            append={props.append}
                            close={props.close}
                        />
                    )}

                    {!props.isHidden && (
                        <WithTooltip
                            tip={isPinned ? 'Unpin this channel' : 'Pin this channel'}
                            disable={props.disableTooltips}
                        >
                            <div
                                className={cn(
                                    styles.settingsBtnComp,
                                    isPinned && !props.showControls && styles.mutedStarBtn,
                                )}
                                onClick={ev => pinHandler(ev, team)}
                                onMouseEnter={() => setPinHover(true)}
                                onMouseLeave={() => setPinHover(false)}
                            >
                                <i
                                    className={cn('fa', {
                                        'fa-star': isPinned || pinHover,
                                        'fa-star-o': !isPinned && !pinHover,
                                    })}
                                />
                            </div>
                        </WithTooltip>
                    )}

                    {false && perms.amTeamAdmin(team) && (
                        <div
                            className={cn(styles.settingsBtnComp)}
                            onClick={ev => openTeamSettings(ev, team)}
                        >
                            <i className="fa fa-gear" />
                        </div>
                    )}

                    {false && (
                        <CopyStreamUrl
                            team={team}
                            copyHandler={copyHandler}
                            disableTooltips={props.disableTooltips}
                        />
                    )}
                </>
            )}
        </div>
    )
}

function NewStreamQuestBtn(props) {
    // Build a filter targeting this specific team so the new thread is pre-assigned to it.
    const teamFilter = props.team ? { team_ids: [props.team.id] } : props.filter

    return (
        <WithTooltip tip="New thread in this channel" disable={props.disableTooltips}>
            <NewQuestButton
                panelId={props.panelId}
                filter={teamFilter}
                team={props.team}
                callback={() => props.close()}
                append={props.append}
            >
                <div className={cn(styles.settingsBtnComp)}>
                    <MessageCircle size={14} />
                </div>
            </NewQuestButton>
        </WithTooltip>
    )
}

function CopyStreamUrl(props) {
    const tipped = React.createRef()
    return (
        <div className="control">
            <div className="copy-button-comp">
                <CopyTooltip tip="Copy Channel Url" offset={0} ref={tipped}>
                    <div
                        className={cn(styles.settingsBtnComp)}
                        onClick={ev => props.copyHandler(ev, tipped, props.team)}
                    >
                        <i className="fa fa-link" />
                    </div>
                </CopyTooltip>
            </div>
        </div>
    )
}

function HodlockerIcon(props) {
    return <div className={styles.hodlockerIcon}>HL</div>
}
